import {DocumentNode} from '@apollo/client';
import {
	DefaultButtonCompat as DefaultButton,
	DialogCompat as Dialog,
	DialogFooterCompat as DialogFooter,
	PrimaryButtonCompat as PrimaryButton,
} from 'components/FluentUICompat';

import {EnumDropdown} from 'components/hookForms';
import {convertToRef} from 'helpers';
import React, {useState} from 'react';
import {useForm} from 'react-hook-form';
import {Market, PogisDocument, RegulatoryDocument, WorkflowStatus} from 'types';
import {evictAuditLogs} from '../../RegDocDetailsPage/evictAuditLogs';
import {RegDocAuditLogsInfo} from '../../RegDocDetailsPage/useGetRegDocAuditLogs';
import {useInjectedData} from '../../RegDocDetailsPage/useInjectedData';
import {useUpdateRegulatoryDocumentMutation} from '../../hooks';
import {useRegDocsFormTranslations} from '../../hooks/RegulatoryDocuments.commonHooks';
import {useUpdateGetexRegulationMutation} from 'features/GetexRegulation/hooks/GetexRegulations.generated';
import {
	BasicDocFields,
	FormRegulatoryDoc,
} from './RegulatoryDocumentsForm.types';
import {RegulatoryDocumentFormInfo} from './useRegulatoryDocumentForm';
import {RegulatoryDocumentFormMode} from './useRegulatoryDocumentFormMode';
import {useNavigate} from 'react-router-dom';
import {
	DocumentReferenceType,
	mapWorkflowUserRefs,
} from 'features/RegulatoryDocuments/regulatoryUtils';
import {useCloneRegulatoryDocumentMutation} from 'features/RegulatoryDocuments/hooks/useCloneRegulatoryDocument.generated';
type StartPolling = (pollInterval: number) => void;

export interface RegulatoryDocumentsFormProps
	extends RegulatoryDocumentFormInfo {
	regulatoryDocuments: BasicDocFields[];
	regulatoryDocument: FormRegulatoryDoc;
	markets: Market[];
	regulationNumber: string;
	regulationId: string | null;
	startPolling: StartPolling | null;
	queriesToRefetchAfterSave: DocumentNode[];
	fetchAndSetAuditLogs?: RegDocAuditLogsInfo['refetchAuditLogsAndUpdateIsLoading'];
	documentReferences: DocumentReferenceType[];
}

enum ChangeType {
	NONE = 'NONE',
	CONTENT = 'CONTENT',
	EDITORIAL = 'EDITORIAL',
}
export const RegulationChangeDialog = ({
	data,
	showConfirmationDialog,
	setShowConfirmationDialog,
	regulatoryDocument,
	regulationId,
	fetchAndSetAuditLogs,
	showTopicManagementSummaryState,
	onRegulatoryDocumentSaved,
	setMode,
	documentReferences,
}: any) => {
	const [changeType, setChangeType] = useState(ChangeType.NONE);

	const [confirmDisabled, setConfirmDisabled] = useState(false);
	const navigate = useNavigate();
	const [updateRegulatoryDocument] = useUpdateRegulatoryDocumentMutation();
	const [updateGetexRegulation] = useUpdateGetexRegulationMutation();
	const [cloneRegulatoryDocumentMutation] =
		useCloneRegulatoryDocumentMutation();

	const {isGetexInjected, getUpdateGetexInput} = useInjectedData(
		regulatoryDocument?.id ?? '',
		!showConfirmationDialog,
	);

	const onDismiss = () => {
		setChangeType(ChangeType.NONE);
		setShowConfirmationDialog(false);
	};

	const getUpdateInput = (
		id: string,
		isFinalized: boolean,
		changeType: ChangeType,
	) => {
		let workflowInput: any = {
			status: WorkflowStatus.Modified,
			message:
				changeType === ChangeType.EDITORIAL
					? regulatoryDocument?.workflow?.message
					: undefined,
		};

		if (isFinalized) {
			workflowInput = {
				status:
					changeType === ChangeType.CONTENT
						? WorkflowStatus.Modified
						: data.workflow?.status,
				message: data.workflow?.message,
				reapprovingMessage: data.workflow?.reapprovingMessage,
				dueDate: data.workflow?.dueDate,
				dueDateInProcess: data.workflow?.dueDateInProcess,
				clearingTimestamp: data.workflow?.clearingTimestamp,
				clearingWithReservationsTimestamp:
					data.workflow?.clearingWithReservationsTimestamp,
				...mapWorkflowUserRefs(data.workflow),
			};
		}

		return {
			id,
			regulationRef: regulationId ? {id: regulationId} : undefined,
			name: data.name,
			status: data.status,
			summary: data.summary,
			workflow: workflowInput,
			finalVersionRef: isFinalized
				? undefined
				: convertToRef(regulatoryDocument.finalVersionRef),
			topicManagementSummary:
				data.topicManagementSummary?.quarter &&
				data.topicManagementSummary?.year &&
				showTopicManagementSummaryState
					? {
							quarter: data.topicManagementSummary?.quarter,
							year: data.topicManagementSummary?.year,
					  }
					: null,
			predecessorRefs: ((data.predecessors ?? []) as RegulatoryDocument[])
				.filter(x => x.id !== undefined)
				.map(x => convertToRef(x)!),
			documentSourceRef: convertToRef(data.documentSource),
			dateEffective: data.dateEffective,
			dateExpiration: data.dateExpiration,
			documentStatus: regulatoryDocument.documentStatus,
			documentReferences: documentReferences,
			disassemblyStatus: data.disassemblyStatus,
			pogisExists: data.pogisExists,
			pogisRefs: ((data.pogisDocuments ?? []) as PogisDocument[])
				.filter(x => x.id !== undefined)
				.map(x => convertToRef(x)!),
			getexLink: data.getexLink,
		};
	};

	const onClearingClick = async () => {
		if (!regulatoryDocument?.id) {
			return;
		}

		setConfirmDisabled(true);
		const isFinalized = data?.workflow?.status === WorkflowStatus.Finalized;

		if (isGetexInjected) {
			await updateGetexRegulation({
				variables: {
					input: getUpdateGetexInput(regulatoryDocument.id, data),
				},
			}).then(result => {
				const regDocId =
					result.data?.updateGetexRegulation?.getexRegulation?.id;
				onRegulatoryDocumentSaved(regDocId);
			});
			await fetchAndSetAuditLogs?.();
		} else if (isFinalized && changeType === ChangeType.EDITORIAL) {
			await updateRegulatoryDocument({
				variables: {
					input: getUpdateInput(regulatoryDocument.id, isFinalized, changeType),
				},
				update: evictAuditLogs,
			}).then(result => {
				const regDocId =
					result.data?.updateRegulatoryDocument?.regulatoryDocument?.id;
				onRegulatoryDocumentSaved(regDocId);
			});
			await fetchAndSetAuditLogs?.();
		} else if (isFinalized && changeType === ChangeType.CONTENT) {
			const cloneResult = await cloneRegulatoryDocumentMutation({
				variables: {
					input: {
						originalRegulatoryDocumentId: regulatoryDocument.id,
						workflowStatus: WorkflowStatus.Modified,
					},
				},
			});
			const clonedRegDoc =
				cloneResult?.data?.cloneRegulatoryDocument?.regulatoryDocument;

			if (!clonedRegDoc) {
				return;
			}

			await updateRegulatoryDocument({
				variables: {
					input: getUpdateInput(clonedRegDoc.id, isFinalized, changeType),
				},
				update: evictAuditLogs,
			}).then(async result => {
				const regDocId =
					result.data?.updateRegulatoryDocument?.regulatoryDocument?.id;
				await onRegulatoryDocumentSaved(regDocId);
			});

			navigate(`/regulations/${regulationId}`);
		} else if (!isFinalized && changeType !== ChangeType.NONE) {
			await updateRegulatoryDocument({
				variables: {
					input: getUpdateInput(regulatoryDocument.id, isFinalized, changeType),
				},
				update: evictAuditLogs,
			}).then(async result => {
				const regDocId =
					result.data?.updateRegulatoryDocument?.regulatoryDocument?.id;
				await onRegulatoryDocumentSaved(regDocId);
			});
			await fetchAndSetAuditLogs?.();
		}
		setConfirmDisabled(false);
		setChangeType(ChangeType.NONE);
		setShowConfirmationDialog(false);
		setMode(RegulatoryDocumentFormMode.None);
	};

	const {control, getValues} = useForm<any>({
		reValidateMode: 'onSubmit',
		mode: 'all',
	});
	const onChangeTypeChange = () => {
		const changeType = getValues('changeType');

		setChangeType(changeType);
	};

	const {t} = useRegDocsFormTranslations();

	return (
		<Dialog
			hidden={!showConfirmationDialog}
			title={t('AcceptChangesTitle')}
			modalProps={{
				isBlocking: true,
				isDarkOverlay: true,
				styles: {
					main: {
						width: '400px !important',
						maxWidth: '400px !important',
					},
				},
			}}
			onDismiss={onDismiss}
		>
			<EnumDropdown
				required={true}
				control={control}
				name={'changeType'}
				label={t('ChangeTypeLabel')}
				placeholder={t('ChooseChangeType')}
				defaultValue={changeType}
				enumName={'ChangeType'}
				enumType={ChangeType}
				exclude={[ChangeType.NONE]}
				onChange={onChangeTypeChange}
			/>

			{changeType === ChangeType.CONTENT && (
				<>
					{' '}
					<br />
					{t('ContentChangeDescription')}
					<br />
					{t('ContentChangeSubdescription')}
				</>
			)}
			<br />
			<DialogFooter>
				<PrimaryButton
					onClick={onClearingClick}
					disabled={confirmDisabled || changeType === ChangeType.NONE}
				>
					{t('Update')}
				</PrimaryButton>
				<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
			</DialogFooter>
		</Dialog>
	);
};
